import { Image, Modal, Platform, StyleSheet, ScrollView, View, Pressable, Share, useWindowDimensions } from "react-native";
import { FindifyTopBarOverlay } from "./components/FindifyTopBarOverlay";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BasePage } from "./helpers/BasePage";
import { PlaceColors } from "./helpers/PlaceColors";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { FindifySimpleHeader, SimpleHeaderAction } from "./components/FindifySimpleHeader";
import { FindifyHTMLRender } from "./helpers/FindifyHTMLRender";
import { PlaceText } from "./components/PlaceText";
import { useTranslation } from "react-i18next";
import { BottomNavigationPadding } from "./components/FindifyNavigation";
import { FindifyLoadingScreen } from "./components/FindifyLoadingScreen";
import { IconShadow } from "./helpers/DefaultShadows";

import { MakeContentDetailsContentVars, MakeContentDetailsSetActions, PlaceContentDetailsInitialUseEffect, makeDateString } from "@/layouts/genericPages/helpers/DetailsHelpers";
import { ContentDetailsGallery } from "@/layouts/genericPages/components/ContentDetailsGallery";
import RNMapbox, { StyleURL } from "@rnmapbox/maps";
import Icon from "react-native-vector-icons/Ionicons";
import { getTranslatedString } from "./helpers/FindifyTranslatedString";
import { getFileUrl } from "@/config/environment";
import { getApiFromModule } from "./helpers/GetContentsByModule";
import { BiscainhosAppHeader } from "./components/BiscainhosAppHeader";
import { useSeoTitle } from "./helpers/SeoTitle";
import { getWorkspaceTitle } from "./helpers/WorkspaceText";

export const PlaceContentDetailsTab = ({ navigation, ...props }: any) => {
    const [contentModule, setContentModule] = useState<any>();
    const [contentSlug, setContentSlug] = useState<any>();
    const [hideDate, setHideDate] = useState(false);
    const { t } = useTranslation();
    const [content, setContent] = useState<any>();
    const findifyDataContext = useContext(FindifyDataContext);

    useEffect(() => {
        PlaceContentDetailsInitialUseEffect(props, findifyDataContext, setContent, setContentSlug, setContentModule, setHideDate);
    }, [])


    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        {content && makeContentDetails(MakeContentDetailsPage, content, t, hideDate, contentModule, navigation, findifyDataContext)}
        {!content && <FindifyLoadingScreen />}
    </BasePage>;
};


export const makeContentDetails = (MakeContentDetailsPage: JSX.ElementType, content: any, t: any, hideDate: boolean, module: string, navigation: any, findifyDataContext: any, isLogged?: boolean) => {
    const {
        image,
        title,
        resume,
        description,
        dateString,
        rightActions,
        bottomButtons,
    } = MakeContentDetailsContentVars(content, module, findifyDataContext, t, navigation);

    return <MakeContentDetailsPage
        image={image}
        title={title}
        resume={resume}
        description={description}
        dateString={dateString}
        hideDate={hideDate}
        navigation={navigation}
        rightActions={rightActions}
        content={content}
        module={module}
        findifyDataContext={findifyDataContext}
        t={t}
        bottomButtons={bottomButtons}
        isLogged={isLogged}
    />
}



type ContentDetailsType = {
    image: string,
    title: string,
    resume: string,
    description: string,
    dateString: string,
    hideDate: boolean,
    navigation: any,
    rightActions: JSX.Element,
    bottomButtons: JSX.Element,
    content: any,
    findifyDataContext: any,
    module: string,
    t: any,
}
const MakeContentDetailsPage = ({ image, title, navigation, resume, description, dateString, rightActions, content, t, hideDate, module, findifyDataContext, bottomButtons }: ContentDetailsType) => {
    const topTextStyle = {
        marginTop: 5,
        fontSize: 14,
        color: PlaceColors(findifyDataContext).text.withAlpha(160)
    };
    const [actionsList, setActionsList] = useState<SimpleHeaderAction[]>([]);
    const [textSizeModifier, setTextSizeModifier] = useState<number>(0);
    useSeoTitle(title, getWorkspaceTitle(findifyDataContext));

    useEffect(() => {
        MakeContentDetailsSetActions(setActionsList, title, findifyDataContext, content, module);
    }, [module])

    if (content?.__loadError) {
        return <View style={[styles.errorContainer, { backgroundColor: PlaceColors(findifyDataContext).background.get() }]}>
            <PlaceText style={[styles.errorTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                {t('base.error') || 'Error'}
            </PlaceText>
            <PlaceText style={[styles.errorText, { color: PlaceColors(findifyDataContext).text.get() }]}>
                Não foi possível carregar este conteúdo.
            </PlaceText>
        </View>
    }

    const videoUrl = getContentVideoUrl(content);

    if (module === 'contents' && findifyDataContext?.workspace?.workspace_id === 156 && isReviewContent(content)) {
        return <BiscainhosReviewDetails
            content={content}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            bottomButtons={bottomButtons}
        />
    }

    if (module === 'contents' && findifyDataContext?.workspace?.workspace_id === 156) {
        return <BiscainhosGenericContentDetails
            content={content}
            image={image}
            title={title}
            resume={resume}
            description={description}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            videoUrl={videoUrl}
            bottomButtons={bottomButtons}
            t={t}
        />
    }

    if (module === 'points_of_interest' && findifyDataContext?.workspace?.workspace_id === 156) {
        return <BiscainhosPoiDetails
            content={content}
            image={image}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            videoUrl={videoUrl}
            bottomButtons={bottomButtons}
        />
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.container, { backgroundColor: PlaceColors(findifyDataContext).background.get() }]}>
            <FindifySimpleHeader
                navigation={navigation}
                title={title}
                actions={actionsList}
                image={image} />
            <View style={[{
                backgroundColor: PlaceColors(findifyDataContext).background.get(),
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                marginTop: -24,
                padding: 20,
                paddingBottom: 20 + BottomNavigationPadding,
            }]}>
                <View style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        {!hideDate &&
                            <PlaceText style={{
                                fontSize: 14,
                                color: PlaceColors(findifyDataContext).text.withAlpha(200),
                                fontWeight: '500',
                            }}>{dateString}</PlaceText>
                        }
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(20), borderRadius: 20, paddingHorizontal: 8, paddingVertical: 4 }}>
                            <Pressable onPress={() => setTextSizeModifier(prev => Math.max(prev - 2, -4))} style={{ paddingHorizontal: 8 }}>
                                <PlaceText style={{ fontSize: 16, fontWeight: 'bold', color: PlaceColors(findifyDataContext).primary.get() }}>A-</PlaceText>
                            </Pressable>
                            <View style={{ width: 1, height: 16, backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(50) }} />
                            <Pressable onPress={() => setTextSizeModifier(prev => Math.min(prev + 2, 8))} style={{ paddingHorizontal: 8 }}>
                                <PlaceText style={{ fontSize: 18, fontWeight: 'bold', color: PlaceColors(findifyDataContext).primary.get() }}>A+</PlaceText>
                            </Pressable>
                        </View>
                        {rightActions}
                    </View>
                </View>
                
                {/* address / resume */}
                {!!resume && <PlaceText style={[topTextStyle, { fontSize: 15 + (textSizeModifier * 0.5), marginBottom: 15, fontWeight: '600', color: PlaceColors(findifyDataContext).text.get() }]}>{resume}</PlaceText>}

                {videoUrl && <ContentVideoPlayer videoUrl={videoUrl} />}

                <FindifyHTMLRender
                    baseStyles={{
                        color: PlaceColors(findifyDataContext).text.get(),
                        fontSize: 16 + textSizeModifier,
                        lineHeight: (16 + textSizeModifier) * 1.5,
                    }}
                    tagsStyles={{ p: { marginBottom: 10 } }}
                    classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                    htmlCode={description} />

                {module === 'routes' && <RouteDetailsMap content={content} findifyDataContext={findifyDataContext} navigation={navigation} />}

                {content.galleries && <View style={{ marginTop: 20 }}><ContentDetailsGallery findifyDataContext={findifyDataContext} t={t} content={content} navigation={navigation} /></View>}
            </View >
        </ScrollView>
        {bottomButtons}
    </>
}

const getContentVideoUrl = (content: any) => {
    const mediaPath = content?.file_video_media?.file_path
        || content?.video_media?.file_path
        || content?.file_video?.file_path;

    if (mediaPath) return getFileUrl() + mediaPath;
    if (typeof content?.file_video === 'string' && content.file_video.startsWith('/')) return getFileUrl() + content.file_video;
    if (typeof content?.file_video === 'string' && content.file_video.startsWith('http')) return content.file_video;

    // The public POI endpoint currently returns only the media id for this file.
    if (+content?.file_video === 5080) return getFileUrl() + '/155/mb_salao_nobre.mp4';

    return undefined;
}

const ContentVideoPlayer = ({ videoUrl }: { videoUrl: string }) => {
    if (Platform.OS === 'web') {
        return <View style={styles.videoContainer}>
            {React.createElement('video', {
                src: videoUrl,
                controls: true,
                playsInline: true,
                style: {
                    width: '100%',
                    height: '100%',
                    borderRadius: 14,
                    backgroundColor: '#000',
                    objectFit: 'contain',
                },
            })}
        </View>
    }

    return null;
}

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .trim();
}

const titleCase = (text: string) => {
    return (text ?? '').toLocaleLowerCase('pt-PT').replace(/(^|\s|\/)(\p{L})/gu, (match) => match.toLocaleUpperCase('pt-PT'));
}

const getPoiTitle = (content: any, language: string) => {
    return titleCase(content?.title?.[language] ?? content?.title?.['en-US'] ?? content?.title?.['pt-PT'] ?? '');
}

const getPoiDescription = (content: any, language: string) => {
    return stripHtml(content?.description?.[language] ?? '');
}

const isEnglishLanguage = (language?: string) => language?.startsWith('en');
const localizedText = (findifyDataContext: any, pt: string, en: string) => (
    isEnglishLanguage(findifyDataContext?.language) ? en : pt
);

const getCategoryParentId = (category: any) => {
    const parentId = category?.parent_category_id
        ?? category?.parent?.category_id
        ?? category?.parent_category?.category_id;
    return parentId == null || parentId === '' ? undefined : Number(parentId);
};

const getCategoryShortTitle = (category: any, findifyDataContext: any) => {
    const title = getTranslatedString(category?.name, findifyDataContext);
    const parts = `${title ?? ''}`.split('#').filter(Boolean).map((part: string) => part.trim());
    return parts.at(-1) || title;
};

const isReviewContent = (content: any) => {
    return content?.categories?.some((line: any) => line?.category_id === 110 || line?.category?.category_id === 110 || line?.category?.page_slug === 'review');
}

const getReviewFormId = (content: any) => {
    const description = Object.values(content?.description ?? {}).join(' ');
    const match = description.match(/data-lexical-form-id=["'](\d+)["']/);
    return match?.[1] ?? '40';
}

const ratingIcons = [
    'sad-outline',
    'sad',
    'remove-circle-outline',
    'happy-outline',
    'happy',
];

const BiscainhosReviewDetails = ({ content, navigation, findifyDataContext, bottomButtons }: any) => {
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const [formData, setFormData] = useState<any>(null);
    const [ratings, setRatings] = useState<Record<string, number>>({});
    const [submitted, setSubmitted] = useState(false);
    const formId = getReviewFormId(content);
    const selectedLanguage = findifyDataContext?.language ?? 'pt-PT';
    useEffect(() => {
        const formsApi = getApiFromModule('forms', findifyDataContext);
        formsApi?.get(formId, null).then((form: any) => {
            form?.questions?.sort((qA: any, qB: any) => Number(qA.sequence ?? 0) - Number(qB.sequence ?? 0));
            setFormData(form);
        }).catch((error: any) => {
            console.error('Failed to load review form:', error);
        });
    }, [formId, findifyDataContext?.workspace?.workspace_id]);

    const submitReview = () => {
        if (!formData) return;
        const formsApi = getApiFromModule('forms', findifyDataContext);
        const questionResponses = (formData.questions ?? [])
            .filter((question: any) => ratings[question.form_question_id] !== undefined)
            .map((question: any) => ({
                form_question_id: question.form_question_id,
                language: findifyDataContext.language,
                response: ratings[question.form_question_id],
                choices: [],
            }));

        formsApi?.create({
            form_id: formId,
            language: findifyDataContext.language,
            question_responses: questionResponses,
        }, null).then(() => {
            setSubmitted(true);
        });
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.reviewPage, { backgroundColor: colors.background.get() }]}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <View style={[
                styles.reviewContent,
                isTablet ? styles.reviewContentTablet : null,
                isLandscapeTablet ? styles.reviewContentLandscapeTablet : null,
            ]}>
                <View style={styles.reviewTitleBlock}>
                    <PlaceText style={[styles.reviewTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Avaliação', 'Review')}</PlaceText>
                    <PlaceText style={[styles.reviewLead, { color: colors.text.withAlpha(200) }]}>{localizedText(findifyDataContext, 'A sua opinião é valiosa para nós.', 'Your feedback is valuable to us.')}</PlaceText>
                </View>

                {(formData?.questions ?? []).map((question: any, index: number) => {
                    const selectedValue = ratings[question.form_question_id];
                    const questionText = question.question?.[selectedLanguage]
                        ?? (isEnglishLanguage(selectedLanguage) ? question.question?.['en-US'] ?? question.question?.['en-GB'] : question.question?.['pt-PT'])
                        ?? getTranslatedString(question.question, findifyDataContext);
                    return <View
                        key={question.form_question_id}
                        style={[
                            styles.reviewQuestionCard,
                            {
                                backgroundColor: colors.background.solidAlphaWhiteBase(28),
                                borderColor: colors.primary.withAlpha(52),
                            }
                        ]}>
                        <PlaceText style={[styles.reviewQuestionTitle, { color: colors.text.get() }]}>{index + 1}. {questionText}</PlaceText>
                        <View style={styles.reviewRatingRow}>
                            {ratingIcons.map((iconName, ratingIndex) => {
                                const value = ratingIndex + 1;
                                const selected = selectedValue === value;
                                return <Pressable
                                    key={iconName + value}
                                    style={styles.reviewRatingButton}
                                    onPress={() => setRatings((current) => ({
                                        ...current,
                                        [question.form_question_id]: value,
                                    }))}>
                                    <Icon
                                        name={iconName}
                                        size={36}
                                        color={selected ? colors.primary.get() : colors.text.withAlpha(105)}
                                        style={{ opacity: selected ? 1 : 0.82 }}
                                    />
                                </Pressable>
                            })}
                        </View>
                    </View>
                })}

                <Pressable style={[styles.reviewSubmitButton, { backgroundColor: colors.primary.get() }]} onPress={submitReview}>
                    <PlaceText style={[styles.reviewSubmitText, { color: colors.background.get() }]}>{submitted ? localizedText(findifyDataContext, 'AVALIAÇÃO SUBMETIDA', 'REVIEW SUBMITTED') : localizedText(findifyDataContext, 'SUBMETER AVALIAÇÃO', 'SUBMIT REVIEW')}</PlaceText>
                </Pressable>

                {findifyDataContext?.workspace?.app_settings?.app_banner_url ? (
                    <Image
                        source={{ uri: findifyDataContext.workspace.app_settings.app_banner_url }}
                        resizeMode="cover"
                        style={styles.reviewBottomImage}
                    />
                ) : null}
            </View>
        </ScrollView>
        {bottomButtons}
    </>
}

const BiscainhosGenericContentDetails = ({
    content,
    image,
    title,
    resume,
    description,
    navigation,
    findifyDataContext,
    videoUrl,
    bottomButtons,
    t,
}: any) => {
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const displayTitle = titleCase(title || getTranslatedString(content?.title ?? content?.name, findifyDataContext) || '');
    const cleanResume = stripHtml(resume);

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.biscainhosPoiPage, { backgroundColor: colors.background.get() }]}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />

            {image ? (
                <View style={[
                    styles.biscainhosPoiHero,
                    isTablet ? styles.biscainhosPoiHeroTablet : null,
                    isLandscapeTablet ? styles.biscainhosPoiHeroLandscapeTablet : null,
                ]}>
                    <Image source={{ uri: image }} resizeMode="cover" style={styles.biscainhosPoiHeroImage} />
                    {videoUrl ? (
                        <View style={[styles.biscainhosHeroPlayHint, { backgroundColor: colors.secondary.get(), borderColor: colors.background.get() }]}>
                            <Icon name="play" size={42} color={colors.primary.get()} />
                        </View>
                    ) : null}
                </View>
            ) : null}

            <View style={[
                styles.biscainhosPoiContent,
                isTablet ? styles.biscainhosPoiContentTablet : null,
                isLandscapeTablet ? styles.biscainhosPoiContentLandscapeTablet : null,
            ]}>
                <View style={[styles.biscainhosPoiTitleCard, { borderLeftColor: colors.primary.get(), backgroundColor: colors.background.solidAlphaBlackBase(18) }]}>
                    <PlaceText style={[styles.biscainhosPoiTitle, { color: colors.primary.get() }]}>{displayTitle}</PlaceText>
                    {!!cleanResume && <PlaceText style={styles.biscainhosPoiSubtitle}>{cleanResume}</PlaceText>}
                </View>

                {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}

                {!!description && <View style={styles.biscainhosPoiLanguageSection}>
                    <FindifyHTMLRender
                        baseStyles={{
                            color: colors.text.get(),
                            fontSize: 17,
                            lineHeight: 27,
                        }}
                        tagsStyles={{ p: { marginBottom: 10 } }}
                        classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                        htmlCode={description}
                    />
                </View>}

                {content.galleries ? (
                    <View style={styles.biscainhosDetailsGallery}>
                        <ContentDetailsGallery findifyDataContext={findifyDataContext} t={t} content={content} navigation={navigation} />
                    </View>
                ) : null}
            </View>
        </ScrollView>
        {bottomButtons}
    </>
}

const BiscainhosPoiDetails = ({ content, image, navigation, findifyDataContext, videoUrl, bottomButtons }: any) => {
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const [videoOpen, setVideoOpen] = useState(false);
    const [nextPoint, setNextPoint] = useState<any>(null);
    const [relatedCategories, setRelatedCategories] = useState<any[]>([]);
    const [relatedPoints, setRelatedPoints] = useState<any[]>([]);
    const title = titleCase(getTranslatedString(content?.title ?? content?.name, findifyDataContext) || '');
    const descriptionHtml = getTranslatedString(content?.description, findifyDataContext) || '';
    const directCategoryIds = (content?.categories ?? [])
        .map((line: any) => Number(line?.category_id ?? line?.category?.category_id))
        .filter(Number.isFinite);
    const directCategoryIdsKey = directCategoryIds.join(',');
    useEffect(() => {
        const api = getApiFromModule('routes', findifyDataContext);
        Promise.all([19, 20].map((routeId) => api?.get(routeId, null, null)))
            .then((routes: any[]) => {
                const routePoints = routes
                    .filter(Boolean)
                    .flatMap((route: any) => (route?.points_of_interest ?? [])
                        .slice()
                        .sort((a: any, b: any) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0)));
                const currentIndex = routePoints.findIndex((routePoint: any) => routePoint?.point_of_interest?.point_of_interest_id === content?.point_of_interest_id);
                setNextPoint(currentIndex >= 0 ? routePoints[currentIndex + 1]?.point_of_interest ?? null : null);
            })
            .catch(() => setNextPoint(null));
    }, [content?.point_of_interest_id, findifyDataContext?.workspace?.workspace_id]);

    useEffect(() => {
        if (!directCategoryIds.length) {
            setRelatedCategories([]);
            setRelatedPoints([]);
            return;
        }

        let isMounted = true;
        const categoriesApi = getApiFromModule('categories', findifyDataContext);
        const pointsApi = getApiFromModule('points_of_interest', findifyDataContext);
        categoriesApi?.getList(0, 300, null, { category_type: 'points_of_interest', active: 1 }, null)
            .then(async (categoriesData: any) => {
                const categories = categoriesData?.results ?? [];
                const byId = new Map(categories.map((category: any) => [Number(category.category_id), category]));
                const childrenByParentId = new Map<number, number[]>();
                categories.forEach((category: any) => {
                    const parentId = getCategoryParentId(category);
                    if (parentId == null || !byId.has(parentId)) return;
                    childrenByParentId.set(parentId, [...(childrenByParentId.get(parentId) ?? []), Number(category.category_id)]);
                });
                const categoryIds = new Set(directCategoryIds);
                const pending = [...directCategoryIds];
                while (pending.length) {
                    const categoryId = pending.shift()!;
                    (childrenByParentId.get(categoryId) ?? []).forEach((childId) => {
                        if (!categoryIds.has(childId)) {
                            categoryIds.add(childId);
                            pending.push(childId);
                        }
                    });
                }
                const selectedCategories = [...categoryIds].map((categoryId) => byId.get(categoryId)).filter(Boolean);
                const pointsData = await pointsApi?.getList(0, 300, null, {
                    'categories.category.category_id_in': JSON.stringify([...categoryIds]),
                }, null);
                if (!isMounted) return;
                setRelatedCategories(selectedCategories);
                setRelatedPoints((pointsData?.results ?? []).filter((point: any) => point?.point_of_interest_id !== content?.point_of_interest_id));
            })
            .catch(() => {
                if (!isMounted) return;
                setRelatedCategories([]);
                setRelatedPoints([]);
            });

        return () => { isMounted = false; };
    }, [content?.point_of_interest_id, directCategoryIdsKey, findifyDataContext?.workspace?.workspace_id]);

    const openPoint = (point: any) => {
        if (!point?.point_of_interest_id) return;
        navigation.push("PlaceContentDetails", {
            module: "points_of_interest",
            content_id: point.point_of_interest_id,
            page_slug: point.page_slug,
        });
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.biscainhosPoiPage, { backgroundColor: colors.background.get() }]}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />

            <View style={[
                styles.biscainhosPoiHero,
                isTablet ? styles.biscainhosPoiHeroTablet : null,
                isLandscapeTablet ? styles.biscainhosPoiHeroLandscapeTablet : null,
            ]}>
                {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.biscainhosPoiHeroImage} /> : null}
                {videoUrl ? (
                    <Pressable style={[styles.biscainhosPoiPlayButton, { backgroundColor: colors.secondary.get(), borderColor: colors.background.get() }]} onPress={() => setVideoOpen(true)}>
                        <Icon name="play" size={42} color={colors.primary.get()} />
                    </Pressable>
                ) : null}
            </View>

            <View style={[
                styles.biscainhosPoiContent,
                isTablet ? styles.biscainhosPoiContentTablet : null,
                isLandscapeTablet ? styles.biscainhosPoiContentLandscapeTablet : null,
            ]}>
                <View style={[styles.biscainhosPoiTitleCard, { borderLeftColor: colors.primary.get(), backgroundColor: colors.background.solidAlphaBlackBase(18) }]}>
                    <PlaceText style={[styles.biscainhosPoiTitle, { color: colors.primary.get() }]}>{title}</PlaceText>
                </View>

                {!!descriptionHtml && <View style={styles.biscainhosPoiLanguageSection}>
                    <FindifyHTMLRender
                        baseStyles={{ color: colors.text.get(), fontSize: 17, lineHeight: 27 }}
                        tagsStyles={{ p: { marginBottom: 10 } }}
                        classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                        htmlCode={descriptionHtml}
                    />
                </View>}

                {relatedCategories.length > 0 ? <View style={[styles.visitExploreSection, { borderColor: colors.primary.withAlpha(42) }]}>
                    <View style={styles.visitExploreHeading}>
                        <Icon name="compass-outline" size={22} color={colors.secondary.get()} />
                        <View style={{ flex: 1 }}>
                            <PlaceText style={[styles.visitExploreTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Explorar nesta paragem', 'Explore this stop')}</PlaceText>
                            <PlaceText style={[styles.visitExploreSubtitle, { color: colors.text.withAlpha(180) }]}>{localizedText(findifyDataContext, 'Sala, núcleo e peças associadas', 'Associated room, nucleus and objects')}</PlaceText>
                        </View>
                    </View>
                    <View style={styles.visitExploreCategories}>
                        {relatedCategories.map((category: any) => <View key={`visit_category_${category.category_id}`} style={[styles.visitExploreCategory, { backgroundColor: colors.primary.withAlpha(18) }]}>
                            <PlaceText style={[styles.visitExploreCategoryText, { color: colors.primary.get() }]}>{getCategoryShortTitle(category, findifyDataContext)}</PlaceText>
                        </View>)}
                    </View>
                    {relatedPoints.map((point: any) => <Pressable
                        key={`visit_point_${point.point_of_interest_id}`}
                        style={[styles.visitExplorePoint, { borderColor: colors.primary.withAlpha(30) }]}
                        onPress={() => openPoint(point)}>
                        <Icon name="cube-outline" size={21} color={colors.primary.get()} />
                        <PlaceText style={[styles.visitExplorePointTitle, { color: colors.text.get() }]}>{titleCase(getTranslatedString(point?.title ?? point?.name, findifyDataContext) || '')}</PlaceText>
                        <Icon name="chevron-forward-outline" size={20} color={colors.primary.get()} />
                    </Pressable>)}
                </View> : null}

                {nextPoint ? (
                    <Pressable style={[styles.biscainhosPoiNextCard, { backgroundColor: colors.primary.get(), borderColor: colors.secondary.get() }]} onPress={() => openPoint(nextPoint)}>
                        <View style={{ flex: 1 }}>
                            <PlaceText style={styles.biscainhosPoiNextTitle}>{localizedText(findifyDataContext, 'Próxima Sala', 'Next Room')}</PlaceText>
                            <PlaceText style={styles.biscainhosPoiNextCopy}>
                                {localizedText(findifyDataContext, 'Sugestão: ', 'Suggested: ')}
                                <PlaceText style={styles.biscainhosPoiNextCopyStrong}>{titleCase(getTranslatedString(nextPoint?.title ?? nextPoint?.name, findifyDataContext))}</PlaceText>
                            </PlaceText>
                        </View>
                        <View style={[styles.biscainhosPoiNextIcon, { backgroundColor: colors.secondary.get() }]}>
                            <Icon name="arrow-forward-outline" size={34} color={colors.primary.get()} />
                        </View>
                    </Pressable>
                ) : null}
            </View>
        </ScrollView>

        <Modal visible={videoOpen} transparent={true} animationType="fade" onRequestClose={() => setVideoOpen(false)}>
            <View style={styles.biscainhosVideoModal}>
                <Pressable style={styles.biscainhosVideoClose} onPress={() => setVideoOpen(false)}>
                    <Icon name="close-outline" size={34} color="#fff" />
                </Pressable>
                {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}
            </View>
        </Modal>
        {bottomButtons}
    </>
}

const styles = StyleSheet.create({
    text: {
        padding: 10,
        paddingBottom: 10 + BottomNavigationPadding,
    },
    container: {
        height: '100%',
        width: '100%',
    },

    headerIcon: {
        padding: 5,
        ...IconShadow,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    errorText: {
        fontSize: 16,
        textAlign: 'center',
    },
    videoContainer: {
        width: '100%',
        height: 240,
        marginBottom: 18,
        borderRadius: 14,
        overflow: 'hidden',
        backgroundColor: '#000',
    },
    biscainhosPoiPage: {
        flex: 1,
        backgroundColor: '#f9f7f2',
    },
    biscainhosPoiHeader: {
        height: 46,
        backgroundColor: '#f9f7f2',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(226, 177, 60, 0.45)',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
    },
    biscainhosPoiHeaderButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosPoiLogo: {
        width: 118,
        height: 30,
    },
    biscainhosPoiHero: {
        aspectRatio: 4 / 5,
        width: '100%',
        backgroundColor: '#1f120f',
        position: 'relative',
        overflow: 'hidden',
    },
    biscainhosPoiHeroTablet: {
        maxWidth: 820,
        alignSelf: 'center',
        width: '100%',
    },
    biscainhosPoiHeroLandscapeTablet: {
        aspectRatio: 16 / 9,
        maxWidth: 1100,
    },
    biscainhosPoiHeroImage: {
        width: '100%',
        height: '100%',
        opacity: 0.92,
    },
    biscainhosPoiPlayButton: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -40,
        marginTop: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 2,
        borderColor: '#f5c443',
        backgroundColor: 'rgba(245, 196, 67, 0.86)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        shadowColor: '#000',
        shadowOpacity: 0.32,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    biscainhosHeroPlayHint: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -40,
        marginTop: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 2,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        shadowColor: '#000',
        shadowOpacity: 0.32,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    biscainhosPoiHeroCaption: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 24,
        paddingTop: 18,
        paddingBottom: 24,
        backgroundColor: 'rgba(153, 0, 0, 0.72)',
    },
    biscainhosPoiHighlightLabel: {
        color: '#f5c443',
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    biscainhosPoiHighlightTitle: {
        marginTop: 4,
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 27,
        lineHeight: 32,
        fontWeight: '700',
    },
    biscainhosPoiContent: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: BottomNavigationPadding + 118,
    },
    biscainhosPoiContentTablet: {
        width: '100%',
        maxWidth: 820,
        alignSelf: 'center',
        paddingHorizontal: 40,
        paddingTop: 34,
    },
    biscainhosPoiContentLandscapeTablet: {
        maxWidth: 980,
    },
    biscainhosPoiTitleCard: {
        backgroundColor: 'rgba(232, 229, 222, 0.72)',
        borderLeftWidth: 4,
        borderLeftColor: '#990000',
        paddingHorizontal: 16,
        paddingVertical: 14,
        marginBottom: 24,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
    },
    biscainhosPoiTitle: {
        color: '#990000',
        fontFamily: 'Georgia',
        fontSize: 29,
        lineHeight: 34,
        fontWeight: '700',
    },
    biscainhosPoiSubtitle: {
        marginTop: 4,
        color: '#3a2a28',
        fontSize: 17,
        lineHeight: 23,
        fontStyle: 'italic',
    },
    biscainhosPoiLanguageSection: {
        marginBottom: 28,
    },
    biscainhosPoiEnglishSection: {
        borderTopWidth: 1,
        borderTopColor: 'rgba(153, 0, 0, 0.22)',
        paddingTop: 20,
    },
    biscainhosPoiLanguageTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },
    biscainhosPoiLanguageTitle: {
        color: '#990000',
        fontSize: 12,
        lineHeight: 16,
        letterSpacing: 3,
        fontWeight: '700',
    },
    biscainhosPoiParagraph: {
        color: '#241716',
        fontSize: 17,
        lineHeight: 27,
    },
    visitExploreSection: {
        marginBottom: 28,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        paddingVertical: 20,
    },
    visitExploreHeading: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    visitExploreTitle: {
        fontSize: 20,
        lineHeight: 26,
        fontWeight: '700',
    },
    visitExploreSubtitle: {
        marginTop: 2,
        fontSize: 14,
        lineHeight: 20,
    },
    visitExploreCategories: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 14,
        marginBottom: 12,
    },
    visitExploreCategory: {
        borderRadius: 16,
        paddingHorizontal: 11,
        paddingVertical: 6,
    },
    visitExploreCategoryText: {
        fontSize: 13,
        lineHeight: 17,
        fontWeight: '600',
    },
    visitExplorePoint: {
        minHeight: 54,
        borderTopWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 11,
    },
    visitExplorePointTitle: {
        flex: 1,
        fontSize: 16,
        lineHeight: 22,
        fontWeight: '600',
    },
    biscainhosPoiNextCard: {
        marginTop: 2,
        borderRadius: 8,
        borderWidth: 1,
        backgroundColor: '#b00000',
        padding: 20,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
    },
    biscainhosPoiNextTitle: {
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 25,
        lineHeight: 30,
        fontWeight: '700',
    },
    biscainhosPoiNextCopy: {
        marginTop: 5,
        color: 'rgba(255,255,255,0.85)',
        fontSize: 14,
        lineHeight: 19,
    },
    biscainhosPoiNextCopyStrong: {
        color: '#f5c443',
        fontWeight: '700',
    },
    biscainhosPoiNextIcon: {
        width: 54,
        height: 54,
        borderRadius: 27,
        backgroundColor: '#f5c443',
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosDetailsGallery: {
        marginTop: 6,
    },
    biscainhosVideoModal: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.92)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
    },
    biscainhosVideoClose: {
        position: 'absolute',
        top: 18,
        right: 18,
        zIndex: 10,
        width: 46,
        height: 46,
        borderRadius: 23,
        backgroundColor: 'rgba(153, 0, 0, 0.9)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewPage: {
        flex: 1,
    },
    reviewHeader: {
        height: 56,
        borderBottomWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 28,
    },
    reviewHeaderButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewLogo: {
        width: 126,
        height: 32,
    },
    reviewContent: {
        paddingHorizontal: 20,
        paddingTop: 48,
        paddingBottom: BottomNavigationPadding + 118,
    },
    reviewContentTablet: {
        width: '100%',
        maxWidth: 760,
        alignSelf: 'center',
        paddingHorizontal: 40,
    },
    reviewContentLandscapeTablet: {
        maxWidth: 980,
    },
    reviewTitleBlock: {
        alignItems: 'center',
        marginBottom: 46,
    },
    reviewTitle: {
        fontFamily: 'Georgia',
        fontSize: 52,
        lineHeight: 58,
        fontWeight: '700',
        textAlign: 'center',
    },
    reviewLead: {
        marginTop: 22,
        fontSize: 19,
        lineHeight: 26,
        textAlign: 'center',
    },
    reviewLeadItalic: {
        marginTop: 5,
        color: 'rgba(58, 31, 29, 0.74)',
        fontSize: 16,
        lineHeight: 22,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    reviewQuestionCard: {
        minHeight: 204,
        borderWidth: 1,
        borderRadius: 4,
        paddingHorizontal: 24,
        paddingVertical: 24,
        marginBottom: 32,
    },
    reviewQuestionTitle: {
        fontFamily: 'Georgia',
        fontSize: 28,
        lineHeight: 34,
        fontWeight: '700',
    },
    reviewQuestionSubtitle: {
        marginTop: 6,
        color: '#3a1f1d',
        fontSize: 13,
        lineHeight: 18,
        letterSpacing: 2,
        textTransform: 'uppercase',
    },
    reviewRatingRow: {
        marginTop: 42,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reviewRatingButton: {
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewSubmitButton: {
        height: 72,
        borderRadius: 3,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 34,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 7,
        shadowOffset: { width: 0, height: 4 },
    },
    reviewSubmitText: {
        fontFamily: 'Georgia',
        fontSize: 21,
        lineHeight: 27,
        fontWeight: '700',
        textAlign: 'center',
    },
    reviewSubmitSubtext: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 18,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    reviewBottomImage: {
        width: '100%',
        height: 190,
        borderRadius: 3,
        marginTop: 82,
    },
    routeMapSection: {
        marginTop: 22,
        gap: 12,
    },
    routeMapHeader: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    routeMapTitle: {
        fontSize: 22,
        lineHeight: 28,
        fontWeight: 'bold',
    },
    routeMapCount: {
        fontSize: 14,
        lineHeight: 20,
    },
    routeMapContainer: {
        height: 320,
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: '#d7f5fb',
    },
    routeMap: {
        flex: 1,
    },
    routeMapOverlay: {
        position: 'absolute',
        left: 12,
        right: 12,
        bottom: 12,
        minHeight: 72,
        borderRadius: 12,
        padding: 12,
        paddingRight: 34,
        backgroundColor: 'rgba(255,255,255,0.96)',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        zIndex: 1000,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
    },
    routeMapOverlayClose: {
        position: 'absolute',
        right: -8,
        top: -8,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: '#000',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
    },
    routeMapOverlayNumber: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routeMapOverlayNumberText: {
        color: '#fff',
        fontSize: 15,
        lineHeight: 18,
        fontWeight: 'bold',
    },
    routeMapOverlayTitle: {
        fontSize: 15,
        lineHeight: 20,
        fontWeight: '600',
    },
    routeMapOverlaySubtitle: {
        fontSize: 13,
        lineHeight: 18,
        marginTop: 2,
    },
    routeMarker: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routeMarkerText: {
        color: '#fff',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: 'bold',
    },
    routePointsList: {
        gap: 8,
    },
    routePointLine: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
    },
    routePointNumber: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routePointNumberText: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 16,
        fontWeight: 'bold',
    },
    routePointTitle: {
        flex: 1,
        fontSize: 15,
        lineHeight: 20,
    }
})

const getCoordsFromPoint = (pointGeometry: string) => {
    const coordinatesMatch = pointGeometry?.match(/\(([^)]+)\)/);
    if (!coordinatesMatch) return null;

    const coordinatesArray = coordinatesMatch[1].split(' ');
    const latitude = parseFloat(coordinatesArray[0]);
    const longitude = parseFloat(coordinatesArray[1]);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
    return { latitude, longitude };
}

const getRoutePoints = (content: any) => {
    return [...(content?.points_of_interest ?? [])]
        .sort((a: any, b: any) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0))
        .map((line: any, index: number) => {
            const poi = line.point_of_interest ?? line;
            const coords = getCoordsFromPoint(poi?.lat_long);
            if (!coords) return null;

            return {
                ...line,
                point: poi,
                index,
                latitude: coords.latitude,
                longitude: coords.longitude,
            }
        })
        .filter(Boolean);
}

const makeRouteLineShape = (points: any[]) => ({
    type: 'Feature',
    geometry: {
        type: 'LineString',
        coordinates: points.map((point: any) => [point.longitude, point.latitude]),
    },
    properties: {},
});

const makeRouteBounds = (points: any[]) => {
    const latitudes = points.map((point: any) => point.latitude);
    const longitudes = points.map((point: any) => point.longitude);

    return {
        sw: [Math.min(...longitudes), Math.min(...latitudes)],
        ne: [Math.max(...longitudes), Math.max(...latitudes)],
        paddingLeft: 0.00001,
        paddingRight: 0.00001,
        paddingTop: 0.00001,
        paddingBottom: 0.00001,
    };
}

const RouteDetailsMap = ({ content, findifyDataContext, navigation }: any) => {
    const points = useMemo(() => getRoutePoints(content), [content]);
    const [routeShape, setRouteShape] = useState<any>(null);
    const [selectedRoutePoint, setSelectedRoutePoint] = useState<any>(null);

    const fallbackLineShape = useMemo(() => makeRouteLineShape(points), [points]);

    useEffect(() => {
        if (points.length < 2 || content?.geojson) return;

        const coordinates = points.map((point: any) => `${point.longitude},${point.latitude}`).join(';');
        const url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`;

        fetch(url)
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                const geometry = data?.routes?.[0]?.geometry;
                if (!geometry?.coordinates?.length) return;

                setRouteShape({
                    type: 'Feature',
                    geometry,
                    properties: {},
                });
            })
            .catch(() => {
                setRouteShape(null);
            });
    }, [content?.geojson, points]);

    if (points.length === 0) return null;

    const lineShape = content?.geojson
        ? (typeof content.geojson === 'string' ? JSON.parse(content.geojson) : content.geojson)
        : (routeShape ?? fallbackLineShape);
    const bounds = makeRouteBounds(points);
    const centerCoordinate = [
        (bounds.sw[0] + bounds.ne[0]) / 2,
        (bounds.sw[1] + bounds.ne[1]) / 2,
    ];

    return <View style={styles.routeMapSection}>
        <View style={styles.routeMapHeader}>
            <PlaceText style={[styles.routeMapTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                Rota
            </PlaceText>
            <PlaceText style={[styles.routeMapCount, { color: PlaceColors(findifyDataContext).text.withAlpha(180) }]}>
                {points.length} pontos
            </PlaceText>
        </View>
        <View style={styles.routeMapContainer}>
            <RNMapbox.MapView style={styles.routeMap} styleURL={StyleURL.Light}>
                <RNMapbox.Camera
                    bounds={bounds}
                    centerCoordinate={centerCoordinate}
                    zoomLevel={content?.map_zoom ? Number(content.map_zoom) : 15}
                />
                <RNMapbox.ShapeSource id="routeLine" shape={lineShape}>
                    <RNMapbox.LineLayer id="routeLineLayer" sourceID="routeLine" />
                </RNMapbox.ShapeSource>
                {points.map((routePoint: any) => {
                    const title = getTranslatedString(routePoint.point?.title, findifyDataContext);
                    return <RNMapbox.MarkerView
                        key={routePoint.route_point_of_interest_id ?? routePoint.point_of_interest_id}
                        coordinate={[routePoint.longitude, routePoint.latitude]}
                        anchor={{ x: 0.5, y: 0.5 }}
                    >
                        <Pressable
                            onPress={() => {
                                setSelectedRoutePoint(routePoint);
                            }}
                            style={styles.routeMarker}
                        >
                            <PlaceText style={styles.routeMarkerText}>{routePoint.index + 1}</PlaceText>
                        </Pressable>
                    </RNMapbox.MarkerView>
                })}
            </RNMapbox.MapView>
            {!!selectedRoutePoint && <Pressable
                style={styles.routeMapOverlay}
                onPress={() => {
                    navigation.push('PlaceContentDetails', {
                        module: 'points_of_interest',
                        content_id: selectedRoutePoint.point?.point_of_interest_id,
                        page_slug: selectedRoutePoint.point?.page_slug,
                    });
                }}
            >
                <Pressable
                    style={styles.routeMapOverlayClose}
                    onPress={() => setSelectedRoutePoint(null)}
                >
                    <Icon name="close" size={18} color="#fff" />
                </Pressable>
                <View style={styles.routeMapOverlayNumber}>
                    <PlaceText style={styles.routeMapOverlayNumberText}>{selectedRoutePoint.index + 1}</PlaceText>
                </View>
                <View style={{ flex: 1 }}>
                    <PlaceText style={[styles.routeMapOverlayTitle, { color: PlaceColors(findifyDataContext).text.get() }]}>
                        {getTranslatedString(selectedRoutePoint.point?.title, findifyDataContext)}
                    </PlaceText>
                    {!!selectedRoutePoint.point?.address && <PlaceText style={[styles.routeMapOverlaySubtitle, { color: PlaceColors(findifyDataContext).text.withAlpha(170) }]}>
                        {selectedRoutePoint.point.address}
                    </PlaceText>}
                </View>
                <Icon name="chevron-forward" size={20} color={PlaceColors(findifyDataContext).text.withAlpha(140)} />
            </Pressable>}
        </View>
        <View style={styles.routePointsList}>
            {points.map((routePoint: any) => {
                const title = getTranslatedString(routePoint.point?.title, findifyDataContext);
                return <Pressable
                    key={`route_point_${routePoint.route_point_of_interest_id ?? routePoint.point_of_interest_id}`}
                    style={styles.routePointLine}
                    onPress={() => {
                        navigation.push('PlaceContentDetails', {
                            module: 'points_of_interest',
                            content_id: routePoint.point?.point_of_interest_id,
                            page_slug: routePoint.point?.page_slug,
                        });
                    }}
                >
                    <View style={styles.routePointNumber}>
                        <PlaceText style={styles.routePointNumberText}>{routePoint.index + 1}</PlaceText>
                    </View>
                    <PlaceText style={[styles.routePointTitle, { color: PlaceColors(findifyDataContext).text.get() }]}>
                        {title}
                    </PlaceText>
                    <Icon name="chevron-forward" size={18} color={PlaceColors(findifyDataContext).text.withAlpha(120)} />
                </Pressable>
            })}
        </View>
    </View>
}
